// Client for the live just_watch FastAPI service (see api/src/just_watch/api.py).
// Base URL is build-time configurable via REACT_APP_API_BASE_URL — set this to the
// deployed Lambda/API Gateway URL in Cloudflare Pages' build environment; defaults
// to the local dev server for `npm start`.

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';

export interface ModelExplanation {
  alpha: number;
  cf_contribution: number;
  content_contribution: number;
  sequence_contribution: number;
  sequence_driver: string | null;
  feature_contributions: Record<string, number>;
  top_drivers: [string, number][];
  matched_features: string[];
  note: string | null;
  summary: string;
  bars: [string, number][]; // fixed-order (label, contribution) pairs, sums to score
}

export interface ModelRecommendation {
  title_id: string;
  title_name: string;
  genre_primary: string;
  language: string;
  score: number;
  explanation: ModelExplanation;
}

interface RecommendationsResponse {
  user_id: string;
  recommendations: ModelRecommendation[];
}

export async function fetchRecommendations(userId: string, n = 8): Promise<ModelRecommendation[]> {
  const res = await fetch(`${API_BASE_URL}/recommendations/${encodeURIComponent(userId)}?n=${n}`);
  if (!res.ok) {
    throw new Error(`just_watch API returned ${res.status}`);
  }
  const data: RecommendationsResponse = await res.json();
  return data.recommendations;
}

export interface LlmExplanation {
  available: boolean;
  sentence: string | null;
}

// Claude-phrased version of one recommendation's exact explanation — fetched
// lazily (only when the viewer flips the toggle), not preloaded for every row.
// `available: false` covers both "no API key configured" and "the model
// wouldn't ground its answer" — both render the same "not available" message.
export async function fetchLlmExplanation(userId: string, titleId: string): Promise<LlmExplanation> {
  const res = await fetch(
    `${API_BASE_URL}/recommendations/${encodeURIComponent(userId)}/explain-llm/${encodeURIComponent(titleId)}`
  );
  if (!res.ok) {
    throw new Error(`just_watch API returned ${res.status}`);
  }
  return res.json();
}

export interface VoiceCandidate {
  title_id: string;
  title_name: string;
  genre_primary: string;
  language: string;
}

export interface VoiceSuggestion {
  available: boolean;
  transcript: string | null;
  title_id: string | null;
  title_name: string | null;
  reply_text: string | null;
  candidates: VoiceCandidate[] | null;
  action: 'open' | 'recommend' | 'clarify' | 'chat' | 'list';
}

// Voice assistant: uploads a recorded request (see components/VoiceAssistant.tsx)
// and gets back one of three outcomes: a direct pick (the viewer named a real
// title outright), a Claude-grounded pick from the viewer's real
// recommendations, or — if the model can't confidently ground one —
// `candidates` for the viewer to choose from manually instead of a dead end.
// `available: false` covers only a transcription failure (no OpenAI key, or
// the API call itself failing) — the widget shows one "not available right
// now" message for that case, same pattern as fetchLlmExplanation above.
export interface VoiceHistoryTurn {
  role: 'user' | 'assistant';
  text: string;
}

// `history` is the voice session's prior turns (kept in VoiceAssistant's state until the
// overlay is closed) so the model can resolve follow-ups ("something else") in context
// instead of every request starting cold.
export async function fetchVoiceSuggestion(
  userId: string,
  audioBlob: Blob,
  filename: string,
  history: VoiceHistoryTurn[] = []
): Promise<VoiceSuggestion> {
  const formData = new FormData();
  formData.append('user_id', userId);
  formData.append('audio', audioBlob, filename);
  formData.append('history', JSON.stringify(history));
  const res = await fetch(`${API_BASE_URL}/voice-suggest`, { method: 'POST', body: formData });
  if (!res.ok) {
    throw new Error(`just_watch API returned ${res.status}`);
  }
  return res.json();
}

// URL for the spoken-reply audio, streamed directly by the browser's <audio>
// element (set as its `src`) rather than fetched as a base64 blob — playback
// can start as soon as bytes arrive instead of waiting for a fully-synthesized
// clip embedded in the /voice-suggest JSON response.
export function voiceReplyAudioUrl(text: string): string {
  return `${API_BASE_URL}/voice-reply-audio?text=${encodeURIComponent(text)}`;
}

// Best-effort batch upload of captured signals (impressions/clicks/searches/
// dwell/playback) to the just_watch API's /events route, which writes them to
// S3 — see recs/analytics.tsx. Fire-and-forget: failures are swallowed so a
// missing/unreachable API degrades to "captured in-browser only", matching
// the rest of the app's graceful-degradation pattern.
export function postEventBatch(events: Record<string, unknown>[]): void {
  if (events.length === 0) return;
  fetch(`${API_BASE_URL}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => {});
}
